import { useCallback, useEffect, useMemo, useState } from "react";
import { IDynamicFormFieldsJson } from "../../models/user/DynamicForm/DynamicForm";
import { IPolicy } from "../../models/user/dashboard/Dashboard";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  VoiceFormIntentId,
  VoiceFormPolicyContext,
  getVoiceFormById,
} from "./voiceFormRegistry";
import {
  applyPolicyContextToFormSchema,
  fetchVoiceDynamicFormSchema,
  formatVoiceFormLoadError,
} from "./voiceFormLoaders";
import {
  VoiceFormAsyncFns,
  buildAddDriverAsyncFns,
  buildAddVehicleAsyncFns,
} from "./voiceDynamicFormAsyncFns";
import {
  DynamicFieldStatus,
  applyExtractedToFormSchema,
  buildDynamicFieldStatuses,
  extractDynamicFieldsFromTranscript,
  flattenDynamicFields,
  getNextDynamicFieldQuestion,
  getMissingRequiredDynamicFieldLabels,
  getNextMissingDynamicFieldLabel,
} from "./voiceDynamicFormLogic";
import { TranscriptTurn } from "./voiceLiveDemoLogic";

export function policyToContext(policy: IPolicy): VoiceFormPolicyContext {
  return {
    policyId: policy.policyId,
    carrierPolicyNo: policy.carrierPolicyNo,
    lobCode: policy.lobCode,
    carrier: policy.carrier || policy.carrierCode || "",
    system: policy.system || "",
    policyType: policy.policyType,
  };
}

export function pickPolicyForForm(
  policies: IPolicy[],
  formId: VoiceFormIntentId
): IPolicy | null {
  if (!policies.length) return null;
  if (formId === "add_vehicle") {
    const pa = policies.find(
      (p) =>
        p.lobCode === "PA" ||
        (p.policyType || "").toLowerCase().includes("auto")
    );
    return pa || policies[0];
  }
  return policies[0];
}

export function useVoiceDynamicForm(
  formId: VoiceFormIntentId | null,
  transcript: TranscriptTurn[],
  policies: IPolicy[]
) {
  const dispatch = useAppDispatch();
  const driverFormJson = useAppSelector(
    (state) => state.driver.addDriverFormFieldsJson
  );
  const vehicleFormJson = useAppSelector(
    (state) => state.vehicle.addVehicleFormFieldsJson
  );

  const definition = formId ? getVoiceFormById(formId) : undefined;
  const policyId = useMemo(() => {
    if (!formId || !definition?.requiresPolicy) return null;
    const p = pickPolicyForForm(policies, formId);
    return p?.policyId ?? null;
  }, [formId, definition, policies]);

  const policy = useMemo(() => {
    if (!formId || !definition?.requiresPolicy || !policyId) return null;
    const p = pickPolicyForForm(policies, formId);
    return p ? policyToContext(p) : null;
  }, [formId, definition, policyId, policies]);

  const [rawSchema, setRawSchema] = useState<IDynamicFormFieldsJson | null>(
    null
  );
  const [baseSchema, setBaseSchema] = useState<IDynamicFormFieldsJson | null>(
    null
  );
  const [formSchema, setFormSchema] = useState<IDynamicFormFieldsJson | null>(
    null
  );
  const [fieldStatuses, setFieldStatuses] = useState<
    Record<string, DynamicFieldStatus>
  >({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const asyncFns: VoiceFormAsyncFns = useMemo(() => {
    if (!policy || formId !== "add_vehicle") {
      if (formId === "add_driver") return buildAddDriverAsyncFns();
      return { asyncOptionsFunctions: {} };
    }
    return buildAddVehicleAsyncFns(policy);
  }, [formId, policy]);

  useEffect(() => {
    if (!formId || !definition?.loadForm) {
      setRawSchema(null);
      setFormError(null);
      setFormLoading(false);
      return;
    }

    let cancelled = false;
    setFormLoading(true);
    setFormError(null);

    (async () => {
      try {
        const loaded = await fetchVoiceDynamicFormSchema(formId, dispatch, {
          driver: driverFormJson,
          vehicle: vehicleFormJson,
        });
        if (cancelled) return;
        if (!loaded) {
          setFormError("Could not load form from API.");
          setRawSchema(null);
          return;
        }
        setRawSchema(loaded);
      } catch (err) {
        if (!cancelled) {
          setFormError(formatVoiceFormLoadError(err));
          setRawSchema(null);
        }
      } finally {
        if (!cancelled) setFormLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formId, definition, dispatch, driverFormJson, vehicleFormJson]);

  useEffect(() => {
    if (!rawSchema) {
      setBaseSchema(null);
      return;
    }
    setBaseSchema(applyPolicyContextToFormSchema(rawSchema, policy || undefined));
  }, [rawSchema, policy]);

  useEffect(() => {
    if (!baseSchema) {
      setFormSchema(null);
      setFieldStatuses({});
      return;
    }
    const extracted = extractDynamicFieldsFromTranscript(baseSchema, transcript);
    const nextSchema = applyExtractedToFormSchema(baseSchema, extracted);
    const statuses = buildDynamicFieldStatuses(nextSchema, extracted);
    setFormSchema(nextSchema);
    setFieldStatuses(statuses);
  }, [transcript, baseSchema]);

  const buildClientFieldsPayload = useCallback((): Record<string, string> => {
    if (!definition || !baseSchema) return {};
    const schema = formSchema ?? baseSchema;
    const filled: Record<string, string> = {};
    for (const field of flattenDynamicFields(schema)) {
      const val = field.value;
      if (val !== undefined && val !== null && String(val).trim() !== "") {
        filled[field.name] = String(val).trim();
      }
    }
    const nextLabel = getNextMissingDynamicFieldLabel(baseSchema, fieldStatuses);
    const nextQuestion = getNextDynamicFieldQuestion(baseSchema, fieldStatuses);
    const missingRequired = getMissingRequiredDynamicFieldLabels(
      baseSchema,
      fieldStatuses
    );
    const payload: Record<string, string> = {
      formIntent: definition.id,
      formTitle: definition.title,
      dynamicFields: JSON.stringify(filled),
      dynamicFieldLabels: nextLabel || "",
      missingRequiredFields: missingRequired.join(", "),
      agentPromptHint: missingRequired.length
        ? nextQuestion ||
          (nextLabel ? `What is the ${nextLabel.toLowerCase()}?` : "")
        : `I have the ${definition.title.toLowerCase()} details. Is there anything else to add?`,
    };
    if (policy?.carrierPolicyNo) {
      payload.policyNumber = policy.carrierPolicyNo;
    }
    return payload;
  }, [definition, baseSchema, formSchema, fieldStatuses, policy]);

  return {
    definition,
    policy,
    formSchema,
    fieldStatuses,
    formError,
    formLoading,
    asyncFns,
    buildClientFieldsPayload,
  };
}
